import { useState } from 'react';
import setupSteps from '../constants/setup-steps';

function useSetupSteps() {
  const stepsIds = Object.values(setupSteps);
  const firstStepIndex = 0;
  const lastStepIndex = stepsIds.length - 1;
  const initialActiveStepIndex = stepsIds.indexOf(setupSteps.PROJECT);

  const [activeStepIndex, setActiveStepIndex] = useState(initialActiveStepIndex);
  const [passedSteps, setPassedSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [invalidSteps, setInvalidSteps] = useState([]);
  const [disabledSteps, setDisabledSteps] = useState([]);

  const resetSteps = () => {
    setActiveStepIndex(initialActiveStepIndex);
    setPassedSteps([]);
    setCompletedSteps([]);
    setInvalidSteps([]);
    setDisabledSteps([]);
  };

  const handlePassedState = (stepIndex) => {
    const stepId = stepsIds[stepIndex];

    if (!passedSteps.includes(stepId)) {
      setPassedSteps([...passedSteps, stepId]);
    }
  };

  const handleCompletedState = (stepIndex, state) => {
    if (stepIndex === lastStepIndex) {
      return;
    }

    const stepId = stepsIds[stepIndex];

    if (state && !completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }

    if (!state && completedSteps.includes(stepId)) {
      setCompletedSteps(completedSteps.filter((id) => id !== stepId));
    }
  };

  const handleDisabledState = (steps) => {
    if (!steps.length) {
      setDisabledSteps([]);
      return;
    }

    const firstInvalidStep = Math.min(...steps.map((stepId) => stepsIds.indexOf(stepId)));

    setDisabledSteps(stepsIds.slice(firstInvalidStep + 1));
  };

  const handleInvalidState = (stepIndex, state) => {
    const stepId = stepsIds[stepIndex];

    if (state && !invalidSteps.includes(stepId)) {
      const updatedInvalidSteps = [...invalidSteps, stepId];

      setInvalidSteps(updatedInvalidSteps);
      handleDisabledState(updatedInvalidSteps);
    }

    if (!state && invalidSteps.includes(stepId)) {
      const updatedInvalidSteps = invalidSteps.filter((id) => id !== stepId);
      setInvalidSteps(updatedInvalidSteps);
      handleDisabledState(updatedInvalidSteps);
    }
  };

  const handleChange = (targetStepIndex, options = {}) => {
    if (
      targetStepIndex < firstStepIndex ||
      targetStepIndex > lastStepIndex ||
      targetStepIndex === activeStepIndex
    ) {
      return;
    }

    const { markStepAsCompleted = false, markStepAsInvalid = false } = options;

    handlePassedState(activeStepIndex);
    handleCompletedState(activeStepIndex, markStepAsCompleted);
    handleInvalidState(activeStepIndex, markStepAsInvalid);

    const isStepForward = targetStepIndex > activeStepIndex;

    if (isStepForward && markStepAsInvalid) {
      return;
    }

    setActiveStepIndex(targetStepIndex);
  };

  const onChangeStep = (stepId, options = {}) => {
    const targetStepIndex = stepsIds.indexOf(stepId);

    handleChange(targetStepIndex, options);
  };

  const onPrevStep = (options = {}) => {
    const targetStepIndex = activeStepIndex - 1;

    handleChange(targetStepIndex, options);
  };

  const onNextStep = (options = {}) => {
    const targetStepIndex = activeStepIndex + 1;

    handleChange(targetStepIndex, options);
  };

  const steps = stepsIds.map((stepId, i) => ({
    id: stepId,
    marker: i + 1,
    active: i === activeStepIndex,
    passed: passedSteps.includes(stepId),
    completed: completedSteps.includes(stepId),
    invalid: invalidSteps.includes(stepId),
    disabled: disabledSteps.includes(stepId),
    isInteractive: passedSteps.includes(stepId),
  }));

  return {
    steps,
    activeStepId: stepsIds[activeStepIndex],
    isFirstStep: activeStepIndex === firstStepIndex,
    isLastStep: activeStepIndex === lastStepIndex,
    resetSteps,
    onChangeStep,
    onPrevStep,
    onNextStep,
  };
}

export default useSetupSteps;
